// ============================================================
//  src/components/ProductCard.jsx
// ============================================================

import ProductImage from "./ProductImage";
import config from "../config";

/**
 * stockInfo prop shape (optional):
 *   { status: "in_stock" | "low_stock" | "out_of_stock", quantity?: number }
 *
 * If stockInfo is null/undefined the badge is hidden and buttons stay enabled
 * (safe default while the parent is still loading stock data).
 */

const STOCK_BADGE = {
  in_stock:     { label: "✅ In Stock",     color: "#27ae60", bg: "#edfaf1" },
  low_stock:    { label: "⚠️ Low Stock",    color: "#c2410c", bg: "#fff4ed" },
  out_of_stock: { label: "❌ Out of Stock", color: "#dc2626", bg: "#fef2f2" },
};

export default function ProductCard({ product, onClick, onAddToCart, onBuyNow, stockInfo }) {
  const { name, price, rating, reviews,
          badge, color, emoji, image, weight, sizes, prices } = product;

  const displaySize  = (weight && sizes?.includes(weight)) ? weight : (sizes && sizes[0]);
  const displayPrice = prices && displaySize && prices[displaySize] ? prices[displaySize] : price;

  const stockStatus  = stockInfo?.status ?? null;
  const stockMeta    = stockStatus ? (STOCK_BADGE[stockStatus] ?? STOCK_BADGE.in_stock) : null;
  const isOutOfStock = stockStatus === "out_of_stock";

  return (
    <div
      className="card-hover"
      onClick={onClick}
      style={{
        background: "#fff",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
        border: `1px solid ${config.colors.cardBorder}`,
        cursor: "pointer",
        opacity: isOutOfStock ? 0.75 : 1,
        transition: "opacity 0.2s",
      }}
    >
      {/* Image area */}
      <div style={{ position: "relative" }}>
        <ProductImage
          src={image}
          emoji={emoji}
          alt={name}
          color={color}
          height={130}
          fontSize={52}
        />

        {/* Product badge (top-left) */}
        <span style={{
          position: "absolute", top: 8, left: 8,
          background: config.colors.primary, color: "#fff",
          fontSize: 9, fontWeight: 800,
          padding: "3px 8px", borderRadius: 8, letterSpacing: 0.3,
          zIndex: 1,
        }}>
          {badge}
        </span>

        {/* Out-of-stock overlay */}
        {isOutOfStock && (
          <div style={{
            position: "absolute", inset: 0,
            background: "rgba(255,255,255,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 2,
          }}>
            <span style={{
              background: "#dc2626", color: "#fff",
              fontSize: 10, fontWeight: 800,
              padding: "4px 10px", borderRadius: 20, letterSpacing: 0.4,
            }}>
              OUT OF STOCK
            </span>
          </div>
        )}
      </div>

      {/* Details */}
      <div style={{ padding: "10px 11px 13px" }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: config.colors.text, marginBottom: 2, lineHeight: 1.3 }}>
          {name}
        </div>
        <div style={{ fontSize: 10, color: "#bbb", marginBottom: 5 }}>{weight}</div>

        {/* Stock status badge */}
        {stockMeta && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            background: stockMeta.bg, color: stockMeta.color,
            fontSize: 9, fontWeight: 800,
            padding: "3px 8px", borderRadius: 20,
            marginBottom: 6, letterSpacing: 0.2,
          }}>
            {stockMeta.label}
            {stockInfo?.quantity && stockStatus === "low_stock"
              ? ` · ${stockInfo.quantity} left`
              : ""}
          </div>
        )}

        <div style={{ fontFamily: "Domine", fontSize: 18, fontWeight: 800, color: isOutOfStock ? "#bbb" : config.colors.primary }}>
          ₹{displayPrice}
        </div>
        <div style={{ fontSize: 10, color: config.colors.accent, marginTop: 4, fontWeight: 700 }}>
          ⭐ {rating} ({reviews})
        </div>

        {/* Add to Cart */}
        <button
          className="btn-press"
          disabled={isOutOfStock}
          onClick={(e) => {
            e.stopPropagation();
            if (!isOutOfStock) onAddToCart({ ...product, selectedSize: displaySize, price: displayPrice });
          }}
          style={{
            width: "100%",
            background: isOutOfStock ? "#f0f0f0" : config.colors.primary,
            color: isOutOfStock ? "#bbb" : "#fff",
            border: "none", borderRadius: 9, padding: "9px 0",
            fontWeight: 700, fontSize: 11,
            cursor: isOutOfStock ? "not-allowed" : "pointer",
            marginTop: 9,
            transition: "background 0.18s, color 0.18s",
          }}
        >
          + Add to Cart
        </button>

        {/* Buy Now */}
        {onBuyNow && (
          <button
            className="buy-now-btn btn-press"
            disabled={isOutOfStock}
            onClick={(e) => {
              e.stopPropagation();
              if (!isOutOfStock) onBuyNow(product, e);
            }}
            style={{
              marginTop: 10,
              cursor: isOutOfStock ? "not-allowed" : "pointer",
              opacity: isOutOfStock ? 0.45 : 1,
              transition: "opacity 0.18s",
            }}
          >
            BUY NOW »
          </button>
        )}
      </div>
    </div>
  );
}
